import { Box } from '@mui/material';
import { useId } from 'react';

/** Secondary routing colour from the case study artwork; no theme token matches it. */
const ROUTE_BLUE = '#3A6EA5';

const CONNECTOR_PATHS = [
  { d: 'M214 165 H246', tone: 'accent', width: 2.5 },
  { d: 'M352 165 H410', tone: 'accent', width: 2.5 },
  { d: 'M540 147 C580 147 580 54 620 54', tone: 'blue', width: 2 },
  { d: 'M540 147 C580 147 580 159 620 159', tone: 'blue', width: 2 },
  { d: 'M540 147 C580 147 580 264 620 264', tone: 'blue', width: 2 },
  { d: 'M540 183 C580 183 580 66 620 66', tone: 'accent-soft', width: 2 },
  { d: 'M540 183 C580 183 580 171 620 171', tone: 'accent-soft', width: 2 },
  { d: 'M540 183 C580 183 580 276 620 276', tone: 'accent-soft', width: 2 },
  { d: 'M790 60 C850 60 850 165 900 165', tone: 'blue', width: 2.5 },
  { d: 'M790 165 H900', tone: 'blue', width: 2.5 },
  { d: 'M790 270 C850 270 850 165 900 165', tone: 'blue', width: 2.5 },
];

const MENU_OPTIONS = [
  { label: 'Youth intake', y: 40 },
  { label: 'Volunteers', y: 145 },
  { label: 'Donations', y: 250 },
];

/**
 * Hero illustration for the nonprofit Amazon Connect case study: an incoming
 * call routed through the Amazon Connect IVR, a single English / Spanish
 * language step, the main menu, and delivery to the intake team.
 *
 * Colours come from the theme's CSS variables so the artwork follows the
 * active brand. Below 720px the figure scrolls sideways rather than shrinking
 * the labels past legibility.
 *
 * @returns {import('react').ReactElement} The call-flow figure
 */
function CallFlowIllustration() {
  const uid = useId();
  const strokeId = `${uid}-language-stroke`;
  const glowId = `${uid}-glow`;

  return (
    <Box
      component="figure"
      sx={{
        m: 0,
        borderRadius: '10px',
        overflow: 'hidden',
        border: '1px solid var(--color-border)',
        background:
          'linear-gradient(135deg, var(--color-card) 0%, var(--color-background) 60%, var(--color-background-light) 100%)',
        '@keyframes callFlowDash': {
          to: { strokeDashoffset: -28 },
        },
        '& .accent-fill': { fill: 'var(--color-primary)' },
        '& .accent-stroke': { stroke: 'var(--color-primary)' },
        '& .blue-fill': { fill: ROUTE_BLUE },
        '& .blue-stroke': { stroke: ROUTE_BLUE },
        '& .node-fill': { fill: 'var(--color-card)' },
        '& .line-stroke': { stroke: 'var(--color-border)' },
        '& .heading-text': { fill: 'var(--color-heading)' },
        '& .muted-text': { fill: 'var(--color-text-muted)' },
        '& .flowline': {
          fill: 'none',
          strokeDasharray: '6 8',
          '@media (prefers-reduced-motion: no-preference)': {
            animation: 'callFlowDash 1.6s linear infinite',
          },
        },
      }}
    >
      <Box sx={{ overflowX: 'auto' }}>
        <Box
          component="svg"
          viewBox="0 0 1100 330"
          role="img"
          aria-label="Illustration of an incoming call answered by Amazon Connect IVR routing. Callers first choose English or Spanish in a single language step, then select youth intake, volunteers, or donations, and every call is delivered to the intake team."
          sx={{
            display: 'block',
            width: '100%',
            minWidth: { xs: 720, md: 0 },
            height: 'auto',
            fontFamily: "'Manrope', sans-serif",
          }}
        >
          <defs>
            <linearGradient id={strokeId} x1="0" x2="1">
              <stop offset="0" style={{ stopColor: 'var(--color-primary)' }} />
              <stop offset="1" stopColor={ROUTE_BLUE} />
            </linearGradient>
            <radialGradient id={glowId} cx=".5" cy=".5" r=".5">
              <stop
                offset="0"
                style={{ stopColor: 'var(--color-primary)', stopOpacity: 0.35 }}
              />
              <stop
                offset="1"
                style={{ stopColor: 'var(--color-primary)', stopOpacity: 0 }}
              />
            </radialGradient>
          </defs>

          {/* Waveform */}
          <g className="accent-fill" opacity=".55">
            <rect x="150" y="150" width="5" height="30" rx="2" />
            <rect x="162" y="138" width="5" height="54" rx="2" />
            <rect x="174" y="128" width="5" height="74" rx="2" />
            <rect x="186" y="142" width="5" height="46" rx="2" />
            <rect x="198" y="154" width="5" height="22" rx="2" />
          </g>

          {/* Connectors */}
          {CONNECTOR_PATHS.map((path) => (
            <path
              key={path.d}
              className={`flowline ${path.tone === 'blue' ? 'blue-stroke' : 'accent-stroke'}`}
              d={path.d}
              strokeWidth={path.width}
              opacity={path.tone === 'accent-soft' ? 0.75 : 1}
            />
          ))}

          {/* Caller */}
          <circle cx="90" cy="165" r="66" fill={`url(#${glowId})`} />
          <circle
            cx="90"
            cy="165"
            r="44"
            className="node-fill accent-stroke"
            strokeWidth="2"
          />
          <path
            className="accent-fill"
            transform="translate(-20 0)"
            d="M96 146c3-3 7-3 9 0l5 7c2 3 1 6-1 8l-3 3c3 6 8 11 14 14l3-3c2-2 5-3 8-1l7 5c3 2 3 6 0 9l-4 4c-4 4-10 4-15 1-12-7-22-17-29-29-3-5-3-11 1-15z"
          />
          <text
            x="90"
            y="238"
            textAnchor="middle"
            fontSize="14"
            fontWeight="700"
            className="heading-text"
          >
            Incoming call
          </text>

          {/* Amazon Connect */}
          <circle cx="300" cy="165" r="76" fill={`url(#${glowId})`} />
          <circle
            cx="300"
            cy="165"
            r="52"
            className="node-fill accent-stroke"
            strokeWidth="2.5"
          />
          <text
            x="300"
            y="160"
            textAnchor="middle"
            fontSize="13"
            fontWeight="800"
            className="accent-fill"
          >
            AMAZON
          </text>
          <text
            x="300"
            y="178"
            textAnchor="middle"
            fontSize="13"
            fontWeight="800"
            className="accent-fill"
          >
            CONNECT
          </text>
          <text
            x="300"
            y="246"
            textAnchor="middle"
            fontSize="13"
            className="muted-text"
          >
            IVR routing
          </text>

          {/* Language choice */}
          <text
            x="475"
            y="112"
            textAnchor="middle"
            fontSize="11"
            fontWeight="700"
            letterSpacing="1.5"
            className="muted-text"
          >
            LANGUAGE CHOICE
          </text>
          <rect
            x="410"
            y="124"
            width="130"
            height="82"
            rx="10"
            className="node-fill"
            stroke={`url(#${strokeId})`}
            strokeWidth="1.5"
          />
          <text
            x="475"
            y="152"
            textAnchor="middle"
            fontSize="15"
            fontWeight="700"
            className="heading-text"
          >
            English
          </text>
          <line
            x1="430"
            y1="165"
            x2="520"
            y2="165"
            className="line-stroke"
            strokeWidth="1"
          />
          <text
            x="475"
            y="188"
            textAnchor="middle"
            fontSize="15"
            fontWeight="700"
            className="accent-fill"
          >
            Español
          </text>
          <circle cx="540" cy="147" r="4" className="blue-fill" />
          <circle cx="540" cy="183" r="4" className="accent-fill" />

          {/* Main menu */}
          <text
            x="705"
            y="28"
            textAnchor="middle"
            fontSize="11"
            fontWeight="700"
            letterSpacing="1.5"
            className="muted-text"
          >
            MAIN MENU
          </text>
          {MENU_OPTIONS.map((option) => (
            <g key={option.label}>
              <rect
                x="620"
                y={option.y}
                width="170"
                height="40"
                rx="8"
                className="node-fill line-stroke"
              />
              <text
                x="642"
                y={option.y + 25}
                fontSize="14"
                fontWeight="700"
                className="heading-text"
              >
                {option.label}
              </text>
              <circle
                cx="776"
                cy={option.y + 20}
                r="4"
                className="accent-fill"
              />
            </g>
          ))}

          {/* Intake team */}
          <rect
            x="900"
            y="110"
            width="170"
            height="110"
            rx="12"
            className="node-fill blue-stroke"
            strokeWidth="2"
          />
          <text
            x="985"
            y="146"
            textAnchor="middle"
            fontSize="14"
            fontWeight="800"
            className="heading-text"
          >
            Intake team
          </text>
          <text
            x="985"
            y="170"
            textAnchor="middle"
            fontSize="12"
            className="muted-text"
          >
            Transcript + audio
          </text>
          <text
            x="985"
            y="188"
            textAnchor="middle"
            fontSize="12"
            className="muted-text"
          >
            SMS intake link
          </text>
          <text
            x="985"
            y="206"
            textAnchor="middle"
            fontSize="12"
            fontWeight="700"
            className="accent-fill"
          >
            100% captured
          </text>
        </Box>
      </Box>
      <Box
        component="figcaption"
        sx={{
          fontSize: '0.75rem',
          color: 'var(--color-text-muted)',
          px: 2.25,
          py: 1.25,
          borderTop: '1px solid var(--color-border)',
        }}
      >
        Sample Amazon Connect Call Flow
        <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>
          {' '}
          · Swipe to see the full flow
        </Box>
      </Box>
    </Box>
  );
}

export default CallFlowIllustration;
